import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { AmbientEngine } from "@/lib/ambient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SoundContext = createContext(null);
export const useSound = () => useContext(SoundContext);

// strip emoji / non-speech symbols before sending to TTS
const cleanForSpeech = (t) =>
  (t || "")
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

export const SoundProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem("adaptience_sound");
    return saved === null ? true : saved === "true";
  });
  const [narrating, setNarrating] = useState(false);

  const ambientRef = useRef(null);
  const audioRef = useRef(null);
  const cacheRef = useRef(new Map());
  const reqRef = useRef(0);

  if (!ambientRef.current) ambientRef.current = new AmbientEngine();
  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  useEffect(() => {
    ambientRef.current.setEnabled(enabled);
    if (!enabled && audioRef.current) {
      audioRef.current.pause();
      setNarrating(false);
    }
  }, [enabled]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      localStorage.setItem("adaptience_sound", String(next));
      return next;
    });
  }, []);

  const setAmbientScene = useCallback((profile) => {
    ambientRef.current.setScene(profile);
  }, []);

  const unlock = useCallback(() => {
    ambientRef.current.resume();
    const isOn = localStorage.getItem("adaptience_sound") !== "false";
    if (isOn) ambientRef.current.setEnabled(true);
  }, []);

  const chime = useCallback(() => {
    ambientRef.current.chime();
  }, []);

  const click = useCallback(() => {
    ambientRef.current.click();
  }, []);

  const stopNarration = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setNarrating(false);
  }, []);

  // Simulation container unmount: silence ambient + narration so nothing plays on non-simulation pages.
  const stopAll = useCallback(() => {
    try { ambientRef.current.silence(); } catch {}
    reqRef.current += 1;
    stopNarration();
  }, [stopNarration]);

  const narrate = useCallback(async (text) => {
    const clean = cleanForSpeech(text);
    if (!clean || !audioRef.current) return;
    // read latest-enabled directly from storage to avoid stale closure
    const isOn = localStorage.getItem("adaptience_sound") !== "false";
    if (!isOn) return;

    const myReq = ++reqRef.current;
    try {
      let url = cacheRef.current.get(clean);
      if (!url) {
        const res = await fetch(`${API}/simulation/narrate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: clean }),
        });
        if (!res.ok) return;
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        cacheRef.current.set(clean, url);
      }
      if (myReq !== reqRef.current) return; // a newer narration was requested
      const a = audioRef.current;
      a.pause();
      a.src = url;
      a.currentTime = 0;
      a.onended = () => setNarrating(false);
      await a.play();
      setNarrating(true);
    } catch (e) {
      setNarrating(false);
    }
  }, []);

  return (
    <SoundContext.Provider
      value={{ enabled, toggle, unlock, setAmbientScene, chime, click, narrate, stopNarration, stopAll, narrating }}
    >
      {children}
    </SoundContext.Provider>
  );
};

export default SoundProvider;
